import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiBaseUrl } from "@/config/api";
import type {
  ItemRef,
  JointCall,
  JointReviewProject,
  JointReviewStudy,
  Pointer,
  SharedText,
  StudyItem,
} from "@/types/jointReview";

/** Both readers work at once, so the panel re-reads the shared state this often. */
export const JOINT_POLL_MS = 2000;

export const jointReviewKeys = {
  project: (projectId: string) => ["joint-review", projectId] as const,
  study: (projectId: string, studyUid: string) => ["joint-review", projectId, studyUid] as const,
  pointer: (projectId: string) => ["joint-review-pointer", projectId] as const,
  sessionNotes: (projectId: string) => ["joint-review-session-notes", projectId] as const,
};

const projectRoot = (projectId: string) =>
  `${getApiBaseUrl()}/api/labeling/projects/${encodeURIComponent(projectId)}/joint-review`;
const studyRoot = (projectId: string, studyUid: string) =>
  `${getApiBaseUrl()}/api/labeling/projects/${encodeURIComponent(projectId)}/studies/${encodeURIComponent(studyUid)}/joint-review`;

/** The joint review's own route for a study's images (`CaseViewer` `imagesUrl`). */
export const jointReviewImagesUrl = (projectId: string, studyUid: string) =>
  `${studyRoot(projectId, studyUid)}/images`;

/** This reader's items in review order, with each item's `done` and finality. */
export function useJointReviewProject(projectId: string | undefined) {
  return useQuery({
    queryKey: jointReviewKeys.project(projectId ?? ""),
    queryFn: async () => (await axios.get<JointReviewProject>(projectRoot(projectId!))).data,
    enabled: !!projectId,
    refetchInterval: JOINT_POLL_MS,
  });
}

/** One study's masks, marks and items with both readers' calls and the notes. */
export function useJointReviewStudy(projectId: string | undefined, studyUid: string | undefined) {
  return useQuery({
    queryKey: jointReviewKeys.study(projectId ?? "", studyUid ?? ""),
    queryFn: async () => (await axios.get<JointReviewStudy>(studyRoot(projectId!, studyUid!))).data,
    enabled: !!projectId && !!studyUid,
    refetchInterval: JOINT_POLL_MS,
  });
}

/** The shared pointer; `shared` false (a blind review's independent phase,
 * where the backend refuses it) keeps it off. */
export function usePointer(projectId: string | undefined, shared: boolean) {
  return useQuery({
    queryKey: jointReviewKeys.pointer(projectId ?? ""),
    queryFn: async () =>
      (await axios.get<Pointer | null>(`${projectRoot(projectId!)}/pointer`)).data,
    enabled: !!projectId && shared,
    refetchInterval: JOINT_POLL_MS,
  });
}

export function useSetPointer(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (target: ItemRef) =>
      (await axios.put<Pointer>(`${projectRoot(projectId)}/pointer`, target)).data,
    onSuccess: (pointer) => queryClient.setQueryData(jointReviewKeys.pointer(projectId), pointer),
  });
}

export function useSessionNotes(projectId: string | undefined, open: boolean) {
  return useQuery({
    queryKey: jointReviewKeys.sessionNotes(projectId ?? ""),
    queryFn: async () =>
      (await axios.get<SharedText>(`${projectRoot(projectId!)}/session-notes`)).data,
    enabled: !!projectId && open,
    refetchInterval: JOINT_POLL_MS,
  });
}

export function useSaveSessionNotes(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (text: string) =>
      (
        await axios.put<SharedText>(`${projectRoot(projectId)}/session-notes`, {
          text,
        })
      ).data,
    onSuccess: (saved) => queryClient.setQueryData(jointReviewKeys.sessionNotes(projectId), saved),
  });
}

/** Replace one item in the cached study with the backend's copy, and refresh the
 * project's finality so progress and Next see it. */
function useItemWrite<Body>(projectId: string, studyUid: string, path: "calls" | "notes") {
  const queryClient = useQueryClient();
  const key = jointReviewKeys.study(projectId, studyUid);
  return useMutation({
    mutationFn: async (body: Body) =>
      (await axios.post<StudyItem>(`${studyRoot(projectId, studyUid)}/${path}`, body)).data,
    onSuccess: (saved) =>
      queryClient.setQueryData<JointReviewStudy>(key, (current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) => (item.item_id === saved.item_id ? saved : item)),
            }
          : current,
      ),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: jointReviewKeys.project(projectId),
      }),
  });
}

/** A call; a blind review's independent call adds its rating or reason. */
export interface CallBody {
  item_id: string;
  call: JointCall;
  rating?: number;
  reason?: string;
}

export const useSaveCall = (projectId: string, studyUid: string) =>
  useItemWrite<CallBody>(projectId, studyUid, "calls");

export const useSaveNote = (projectId: string, studyUid: string) =>
  useItemWrite<{ item_id: string; text: string }>(projectId, studyUid, "notes");
